import { CloseOutlined } from "@ant-design/icons"
import {
  Alert,
  Button,
  DatePicker,
  Flex,
  Form,
  Input,
  Modal,
  Select,
  Tooltip,
  Typography
} from "antd"
import type { Dayjs } from "dayjs"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { FollowUpTimePicker } from "../../../../../components/Date&TimePickers/FollowUpTimePicker.tsx"
import { LEAD_STATUSES } from "../../../../Lead/constants/lead-statuses.ts"
import {
  isLifecycleStage,
  stageIndex
} from "../../../../Lead/constants/lifecycle-stages.ts"
import {
  OPERATIONAL_STATES,
  withoutRetiredStates,
  type OperationalState
} from "../../../../Lead/constants/operational-states.ts"
import type {
  BulkLeadAction,
  BulkLeadActionParams,
  BulkLeadResult
} from "../../../api/services.types.ts"
import { DELETE_LEAD_ALLOWED_STATUSES } from "../../../constants/delete-lead-allowed-statuses.ts"
import type { LeadsFilterOptions } from "../../../redux/leads.types.ts"

const { Text } = Typography

// Mirrors LEAD_BULK_LIMIT on the server. Sending more comes back as a field
// error the bulk result cannot express, so it is refused before it is sent.
export const LEAD_BULK_LIMIT = 200

/** What a row tells us about whether an action can reach the lead behind it. */
export interface BulkLeadFacts {
  status: number
  converted: boolean
}

type BulkFormValues = {
  manager?: string
  target_status?: number
  operational_states?: OperationalState[]
  follow_up_owner?: string
  follow_up_date?: Dayjs
  follow_up_time?: Dayjs
  lost_reason?: string
  lost_notes?: string
}

interface LeadsBulkActionBarProps {
  selectedIds: string[]
  leadNames: Record<string, string>
  // Absent for a lead whose row has not been on screen, which the counts below
  // treat as eligible — the server is the authority either way.
  leadFacts: Record<string, BulkLeadFacts>
  filterOptions: LeadsFilterOptions
  editAllowed?: boolean
  deleteAllowed?: boolean
  exporting?: boolean
  onClear: () => void
  onAction: (params: BulkLeadActionParams) => Promise<BulkLeadResult>
  onExport: (ids: string[]) => Promise<void>
  /**
   * The bar outlives the selection — it keeps reporting the outcome after an
   * action clears it — so anything anchored to the same corner has to follow
   * this rather than the selection.
   */
  onVisibleChange?: (visible: boolean) => void
}

interface BulkResultProps {
  result: BulkLeadResult
  leadNames: Record<string, string>
}

export function LeadsBulkResult({ result, leadNames }: BulkResultProps) {
  const { t } = useTranslation()

  return (
    <div>
      <Text strong>
        {t("leadBulk.result.summary", {
          succeeded: result.succeeded.length,
          requested: result.requested
        })}
      </Text>
      {/* Bounded because the bar is pinned to the bottom: an unbounded list
          grows upwards over the table rather than overflowing down. */}
      {result.failed.length ? (
        <ul
          data-testid="leads-bulk-result-failures"
          // Nothing inside takes focus, so the region carries it — otherwise
          // the refusals below the cap are unreachable without a pointer.
          tabIndex={0}
          aria-label={t("leadBulk.result.failuresLabel", {
            count: result.failed.length
          })}
          className="mb-0 mt-2 max-h-[30vh] list-disc overflow-y-auto pl-5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2A7D8C]"
        >
          {result.failed.map((failure) => (
            <li key={failure.id}>
              <span>
                {t("leadBulk.result.failure", {
                  lead: leadNames[failure.id] || failure.id,
                  reason: failure.message
                })}
              </span>
              {failure.missing?.length ? (
                <ul className="mb-0 list-disc pl-5">
                  {failure.missing.map((field) => (
                    <li key={field}>{field}</li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

export function LeadsBulkActionBar({
  selectedIds,
  leadNames,
  leadFacts,
  filterOptions,
  editAllowed,
  deleteAllowed,
  exporting,
  onClear,
  onAction,
  onExport,
  onVisibleChange
}: LeadsBulkActionBarProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<BulkFormValues>()
  const [activeAction, setActiveAction] = useState<BulkLeadAction | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState<BulkLeadResult | null>(null)
  // What the last Close Lost sent, so a retry can repeat it verbatim with the
  // confirmation added — the leads it refused are named in `result.failed`.
  const [lastArchive, setLastArchive] = useState<{
    target_status?: number
    lost_reason?: string
    lost_notes?: string
  } | null>(null)
  const [requestFailed, setRequestFailed] = useState(false)

  const actionLabels = useMemo<Record<BulkLeadAction, string>>(
    () => ({
      "assign-owner": t("leadBulk.actions.assignOwner"),
      "change-stage": t("leadBulk.actions.changeStage"),
      "operational-states": t("leadBulk.actions.operationalStates"),
      "follow-up": t("leadBulk.actions.followUp"),
      "close-lost": t("leadBulk.actions.closeLost"),
      delete: t("leadBulk.actions.delete")
    }),
    [t]
  )

  // `filterOptions.status` is the filter row's list: it carries retired stages
  // and, for archive-permission holders, the archived ones. The transition
  // endpoint rejects everything outside the lifecycle, and numeric order puts
  // Booked, Exploration and Contract after Won.
  const stageOptions = useMemo(
    () =>
      (filterOptions.status ?? [])
        .filter((option) => isLifecycleStage(option?.value))
        .sort((a, b) => stageIndex(a?.value) - stageIndex(b?.value)),
    [filterOptions.status]
  )

  const blockedFromDelete = useMemo(
    () =>
      selectedIds.filter((id) => {
        const facts = leadFacts[id]
        if (!facts) return false
        return (
          facts.converted ||
          !DELETE_LEAD_ALLOWED_STATUSES.includes(facts.status)
        )
      }),
    [selectedIds, leadFacts]
  )
  const noneDeletable =
    !!selectedIds.length && blockedFromDelete.length === selectedIds.length

  // Everything else a stage change needs — the target's entry criteria — reads
  // records the list payload does not carry, so only the converted leads can be
  // named up front. The rest come back named in the result.
  const blockedFromStageChange = useMemo(
    () => selectedIds.filter((id) => leadFacts[id]?.converted),
    [selectedIds, leadFacts]
  )
  const noneStageable =
    !!selectedIds.length && blockedFromStageChange.length === selectedIds.length

  const pendingStates = Form.useWatch("operational_states", form)
  const settingFollowUpState =
    activeAction === "operational-states" &&
    !!pendingStates?.includes(OPERATIONAL_STATES.FOLLOW_UP)

  const pendingOwner = Form.useWatch("follow_up_owner", form)
  const pendingDate = Form.useWatch("follow_up_date", form)
  const pendingTime = Form.useWatch("follow_up_time", form)

  // Flagging a batch for chasing is not booking a batch of meetings, so the
  // slot is optional here — until one part of it is filled, at which point the
  // rest is owed: the server refuses a date without a time, and neither
  // schedules anything without an owner. "Follow Up" (bulk-follow-up) is the
  // action that always books, and its own fields stay required.
  const schedulingInBulk = !!(pendingOwner || pendingDate || pendingTime)
  const closeModal = () => {
    if (submitting) return
    setActiveAction(null)
    form.resetFields()
  }

  const openModal = (action: BulkLeadAction) => {
    form.resetFields()
    setActiveAction(action)
  }

  const submitAction = async () => {
    if (!activeAction) return

    // A rejected validation is already reported on the fields themselves; only
    // a failed request needs a message of its own.
    let values: BulkFormValues
    try {
      values = await form.validateFields()
    } catch {
      return
    }

    setSubmitting(true)
    try {
      const nextResult = await onAction({
        action: activeAction,
        ids: selectedIds,
        ...values,
        target_status:
          activeAction === "close-lost"
            ? LEAD_STATUSES.LOST
            : values.target_status,
        follow_up_date: values.follow_up_date?.format("YYYY-MM-DD"),
        follow_up_time: values.follow_up_time?.format("HH:mm")
      })
      setRequestFailed(false)
      setResult(nextResult)
      setLastArchive(
        activeAction === "close-lost"
          ? {
              target_status: LEAD_STATUSES.LOST,
              lost_reason: values.lost_reason,
              lost_notes: values.lost_notes
            }
          : null
      )
      setActiveAction(null)
      form.resetFields()
    } catch {
      setResult(null)
      setRequestFailed(true)
      setActiveAction(null)
      form.resetFields()
    } finally {
      setSubmitting(false)
    }
  }

  const clear = () => {
    setResult(null)
    setRequestFailed(false)
    setLastArchive(null)
    onClear()
  }

  // Leads the server refused because they still have meetings booked. Without
  // a way to answer that here, a mixed selection would leave exactly those
  // leads permanently un-archivable from the bulk bar.
  const blockedByMeetings = (result?.failed ?? []).filter(
    (failure) => failure.type === "pending_commitments"
  )

  const archiveBlockedAnyway = async () => {
    if (!lastArchive || !blockedByMeetings.length) return
    setSubmitting(true)
    try {
      const nextResult = await onAction({
        action: "close-lost",
        ids: blockedByMeetings.map((failure) => failure.id),
        ...lastArchive,
        cancel_appointments: true
      })
      setResult(nextResult)
      setRequestFailed(false)
    } catch {
      setResult(null)
      setRequestFailed(true)
    } finally {
      setSubmitting(false)
    }
  }

  const visible = !!selectedIds.length || !!result || requestFailed

  useEffect(() => {
    onVisibleChange?.(visible)
  }, [visible, onVisibleChange])

  if (!visible) return null

  const overLimit = selectedIds.length > LEAD_BULK_LIMIT

  const buttonClass =
    "!h-9 !border-[#2A7D8C] !bg-[#2A7D8C] !text-white hover:!border-[#236b78] hover:!bg-[#236b78]"

  return (
    <div className="sticky bottom-0 z-30" data-testid="leads-bulk-action-bar">
      {selectedIds.length ? (
        <Flex
          align="center"
          gap="small"
          className="overflow-x-auto bg-[#17383E] px-4 py-3 text-white"
          role="toolbar"
          aria-label={t("leadBulk.toolbarLabel")}
          wrap={false}
        >
          <Text strong className="mr-2 whitespace-nowrap !text-white">
            {t("leadBulk.selected", { count: selectedIds.length })}
          </Text>

          {overLimit ? (
            <Text className="mr-2 whitespace-nowrap !text-amber-300">
              {t("leadBulk.overLimit", { limit: LEAD_BULK_LIMIT })}
            </Text>
          ) : null}

          {editAllowed ? (
            <>
              <Button
                className={buttonClass}
                disabled={overLimit}
                onClick={() => openModal("assign-owner")}
              >
                {actionLabels["assign-owner"]}
              </Button>
              <Button
                className={buttonClass}
                disabled={overLimit}
                onClick={() => openModal("change-stage")}
              >
                {actionLabels["change-stage"]}
              </Button>
              <Button
                className={buttonClass}
                disabled={overLimit}
                onClick={() => openModal("operational-states")}
              >
                {actionLabels["operational-states"]}
              </Button>
              <Button
                className={buttonClass}
                disabled={overLimit}
                onClick={() => openModal("follow-up")}
              >
                {actionLabels["follow-up"]}
              </Button>
              <Button
                className={buttonClass}
                disabled={overLimit}
                onClick={() => openModal("close-lost")}
              >
                {actionLabels["close-lost"]}
              </Button>
            </>
          ) : null}

          <Button
            className={buttonClass}
            loading={exporting}
            onClick={() => onExport(selectedIds)}
          >
            {t("leadBulk.actions.export")}
          </Button>

          {deleteAllowed ? (
            <Button
              danger
              type="primary"
              disabled={overLimit}
              onClick={() => openModal("delete")}
            >
              {actionLabels.delete}
            </Button>
          ) : null}

          <Tooltip title={t("leadBulk.clearSelection")}>
            <Button
              // The fixed support launcher expands to 56px from the right
              // edge. Its trailing margin keeps this dismiss action clickable
              // even while the launcher is open.
              className="ml-auto mr-16 shrink-0 !text-white"
              type="text"
              icon={<CloseOutlined />}
              aria-label={t("leadBulk.clearSelection")}
              onClick={clear}
            />
          </Tooltip>
        </Flex>
      ) : null}

      {/* Below the toolbar, so a result does not push the controls still in
          use further down the page. */}
      {requestFailed ? (
        <Alert
          className="rounded-t-none"
          data-testid="leads-bulk-request-error"
          type="error"
          showIcon
          closable
          onClose={() => setRequestFailed(false)}
          message={t("leadBulk.result.requestFailed")}
        />
      ) : null}

      {result ? (
        <Alert
          className="rounded-t-none"
          type={result.failed.length ? "warning" : "success"}
          showIcon
          closable
          onClose={() => setResult(null)}
          message={<LeadsBulkResult result={result} leadNames={leadNames} />}
          action={
            blockedByMeetings.length ? (
              <Button
                size="small"
                danger
                loading={submitting}
                onClick={archiveBlockedAnyway}
              >
                {t("leadBulk.result.cancelMeetings", {
                  count: blockedByMeetings.length
                })}
              </Button>
            ) : null
          }
        />
      ) : null}

      <Modal
        open={activeAction != null}
        title={activeAction ? actionLabels[activeAction] : undefined}
        okText={
          activeAction === "delete"
            ? t("leadBulk.actions.delete")
            : t("leadBulk.apply")
        }
        cancelText={t("leadBulk.cancel")}
        okButtonProps={{
          danger: activeAction === "delete",
          disabled:
            (activeAction === "delete" && noneDeletable) ||
            (activeAction === "change-stage" && noneStageable)
        }}
        confirmLoading={submitting}
        cancelButtonProps={{ disabled: submitting }}
        closable={!submitting}
        keyboard={!submitting}
        maskClosable={false}
        destroyOnHidden
        onOk={submitAction}
        onCancel={closeModal}
      >
        <Form form={form} layout="vertical" disabled={submitting}>
          {activeAction === "assign-owner" ? (
            <Form.Item<BulkFormValues>
              name="manager"
              label={t("leadBulk.fields.owner")}
              rules={[
                { required: true, message: t("leadBulk.validation.owner") }
              ]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                options={filterOptions.sales_manager}
                placeholder={t("leadBulk.placeholders.owner")}
              />
            </Form.Item>
          ) : null}

          {activeAction === "change-stage" ? (
            <>
              {blockedFromStageChange.length ? (
                <Alert
                  className="mb-4"
                  type="warning"
                  showIcon
                  message={
                    noneStageable
                      ? t("leadBulk.stageNoneEligible", {
                          count: selectedIds.length
                        })
                      : t("leadBulk.stageSomeEligible", {
                          blocked: blockedFromStageChange.length,
                          count: selectedIds.length
                        })
                  }
                  description={t("leadBulk.stageConvertedRule")}
                />
              ) : null}
              <Form.Item<BulkFormValues>
                name="target_status"
                label={t("leadBulk.fields.stage")}
                rules={[
                  { required: true, message: t("leadBulk.validation.stage") }
                ]}
              >
                <Select
                  options={stageOptions}
                  placeholder={t("leadBulk.placeholders.stage")}
                />
              </Form.Item>
              {noneStageable ? null : (
                <Text type="secondary">{t("leadBulk.stageCriteriaHint")}</Text>
              )}
            </>
          ) : null}

          {activeAction === "operational-states" ? (
            <>
              <Form.Item<BulkFormValues>
                name="operational_states"
                label={t("leadBulk.fields.states")}
                rules={[
                  { required: true, message: t("leadBulk.validation.states") }
                ]}
              >
                <Select
                  mode="multiple"
                  optionFilterProp="label"
                  // `filter_options` is built from the backend's live enum, so
                  // until crm/0037 deploys it still offers the retired parking
                  // overlays. Picking one produced a request the server then
                  // refused for every selected lead.
                  options={(filterOptions.operational_states ?? []).filter(
                    (option) =>
                      withoutRetiredStates([String(option.value)]).length > 0
                  )}
                  placeholder={t("leadBulk.placeholders.states")}
                />
              </Form.Item>

              {settingFollowUpState ? (
                <Form.Item<BulkFormValues>
                  preserve={false}
                  name="follow_up_owner"
                  label={t("leadBulk.fields.followUpOwner")}
                  rules={[
                    {
                      required: schedulingInBulk,
                      message: t("leadBulk.validation.followUpOwner")
                    }
                  ]}
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    options={filterOptions.sales_manager}
                    placeholder={t("leadBulk.placeholders.owner")}
                  />
                </Form.Item>
              ) : null}

              {settingFollowUpState ? (
                <Form.Item<BulkFormValues>
                  preserve={false}
                  name="follow_up_date"
                  label={t("leadBulk.fields.followUpDate")}
                  rules={[
                    {
                      required: schedulingInBulk,
                      message: t("leadBulk.validation.followUpDate")
                    }
                  ]}
                >
                  <DatePicker className="w-full" />
                </Form.Item>
              ) : null}

              {settingFollowUpState ? (
                <Form.Item<BulkFormValues>
                  preserve={false}
                  name="follow_up_time"
                  label={t("leadBulk.fields.followUpTime")}
                  rules={[
                    {
                      required: schedulingInBulk,
                      message: t("leadBulk.validation.followUpTime")
                    }
                  ]}
                >
                  <FollowUpTimePicker className="w-full" />
                </Form.Item>
              ) : null}
            </>
          ) : null}

          {activeAction === "follow-up" ? (
            <>
              <Form.Item<BulkFormValues>
                name="follow_up_owner"
                label={t("leadBulk.fields.followUpOwner")}
                rules={[
                  {
                    required: true,
                    message: t("leadBulk.validation.followUpOwner")
                  }
                ]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={filterOptions.sales_manager}
                  placeholder={t("leadBulk.placeholders.owner")}
                />
              </Form.Item>
              <Form.Item<BulkFormValues>
                name="follow_up_date"
                label={t("leadBulk.fields.followUpDate")}
                rules={[
                  {
                    required: true,
                    message: t("leadBulk.validation.followUpDate")
                  }
                ]}
              >
                <DatePicker className="w-full" />
              </Form.Item>
              <Form.Item<BulkFormValues>
                name="follow_up_time"
                label={t("leadBulk.fields.followUpTime")}
                rules={[
                  {
                    required: true,
                    message: t("leadBulk.validation.followUpTime")
                  }
                ]}
              >
                <FollowUpTimePicker className="w-full" />
              </Form.Item>
            </>
          ) : null}

          {activeAction === "close-lost" ? (
            <>
              <Alert
                className="mb-4"
                type="warning"
                showIcon
                message={t("leadBulk.closeLostImpact", {
                  count: selectedIds.length
                })}
              />
              <Form.Item<BulkFormValues>
                name="lost_reason"
                label={t("leadBulk.fields.lossReason")}
              >
                <Input.TextArea
                  rows={3}
                  placeholder={t("leadBulk.placeholders.lossReason")}
                />
              </Form.Item>
              <Form.Item<BulkFormValues>
                name="lost_notes"
                label={t("leadBulk.fields.notes")}
              >
                <Input.TextArea
                  rows={3}
                  placeholder={t("leadBulk.placeholders.notes")}
                />
              </Form.Item>
            </>
          ) : null}

          {activeAction === "delete" ? (
            <>
              {blockedFromDelete.length ? (
                <Alert
                  className="mb-4"
                  type="warning"
                  showIcon
                  message={
                    noneDeletable
                      ? t("leadBulk.deleteNoneEligible", {
                          count: selectedIds.length
                        })
                      : t("leadBulk.deleteSomeEligible", {
                          blocked: blockedFromDelete.length,
                          count: selectedIds.length
                        })
                  }
                  description={t("leadBulk.deleteEligibilityRule")}
                />
              ) : null}
              {noneDeletable ? null : (
                <Alert
                  type="error"
                  showIcon
                  message={t("leadBulk.deleteImpact", {
                    count: selectedIds.length - blockedFromDelete.length
                  })}
                  description={t("leadBulk.deleteExtraWarning")}
                />
              )}
            </>
          ) : null}
        </Form>
      </Modal>
    </div>
  )
}
